import {
  getDefaultPresetTool,
  getPhotoEditorPreviewPoster,
  GO_VIRAL_AVATARS_HREF,
} from "@/app/editor/data/ai-photo-editor";
import {
  getHubFreeToolCards,
  getPhotoEditorGoViralAvatarStyles,
} from "@/components/dashboard/jtbd/jtbdData";
import {
  JTBD_AVATAR_HERO_IMAGES,
  JTBD_HEADSHOT_HERO_IMAGES,
} from "@/components/dashboard/jtbd/jtbdHeroMedia";

export const EDIT_WITH_CHAT_HREF = "/editor/ai-designer";

export type HeroProductId =
  | "headshots"
  | "photoshoot"
  | "edit-chat"
  | "photo-editor"
  | "go-viral"
  | "free";

export type HeroProduct = {
  id: HeroProductId;
  label: string;
  blurb: string;
  href: string;
  images: string[];
  /** Tailwind classes for the small icon tile. */
  iconTile: string;
  icon: string;
  badge?: "NEW" | "HOT" | "FREE" | "POPULAR";
};

export const EDIT_CHAT_SUGGESTIONS = [
  "Remove glasses",
  "Change outfit to navy suit",
  "Make background blurry",
  "Add a natural smile",
];

export const SELFIE_BEFORE = "/assets/images/tools/before.png";
export const SELFIE_AFTER_HEADSHOT = "/assets/images/tools/ai-headshots-3-up-opt.png";
export const SELFIE_AFTER_PHOTOSHOOT = "/assets/images/tools/ai_photoshoot_promo_1.png";
export const EDIT_CHAT_IMAGE = "/assets/images/tools/ai-designer.png";

export function getHeroProducts(): Record<HeroProductId, HeroProduct> {
  const editorPoster = getPhotoEditorPreviewPoster(getDefaultPresetTool().preview);
  const viral = getPhotoEditorGoViralAvatarStyles(4).map((style) => style.image);
  const free = getHubFreeToolCards()
    .flatMap((card) => card.images)
    .filter((src) => !src.startsWith("data:"));

  return {
    headshots: {
      id: "headshots",
      label: "AI Headshots",
      blurb: "515+ studio looks from one selfie",
      href: "/editor-v2/ai-headshots",
      images: JTBD_HEADSHOT_HERO_IMAGES,
      iconTile: "bg-[#173A6E] text-white",
      icon: "💼",
      badge: "POPULAR",
    },
    photoshoot: {
      id: "photoshoot",
      label: "AI Photoshoot",
      blurb: "Lifestyle photos for dating & socials",
      href: "/editor-v2/ai-avatars",
      images: [SELFIE_AFTER_PHOTOSHOOT, ...JTBD_AVATAR_HERO_IMAGES],
      iconTile: "bg-[#C4785A] text-white",
      icon: "📸",
    },
    "edit-chat": {
      id: "edit-chat",
      label: "Edit with Chat",
      blurb: "Edit again and again - your photo stays sharp",
      href: EDIT_WITH_CHAT_HREF,
      images: [EDIT_CHAT_IMAGE],
      iconTile: "bg-[#5956E9] text-white",
      icon: "✨",
      badge: "NEW",
    },
    "photo-editor": {
      id: "photo-editor",
      label: "Photo Editor",
      blurb: "One-tap retouch, beauty & try-on",
      href: "#photo-editor",
      images: editorPoster ? [editorPoster] : [],
      iconTile: "bg-violet-100 text-violet-700",
      icon: "🎨",
      badge: "HOT",
    },
    "go-viral": {
      id: "go-viral",
      label: "Go Viral",
      blurb: "Trending styles for your feed",
      href: GO_VIRAL_AVATARS_HREF,
      images: viral,
      iconTile: "bg-pink-100 text-pink-700",
      icon: "🔥",
    },
    free: {
      id: "free",
      label: "PFP Maker",
      blurb: "Remove background, borders - free",
      href: "/pfpmaker-editor/remove-background",
      images: free,
      iconTile: "bg-[#69C85A] text-white",
      icon: "🟢",
      badge: "FREE",
    },
  };
}

export const BADGE_CLASS: Record<NonNullable<HeroProduct["badge"]>, string> = {
  NEW: "bg-emerald-500 text-white",
  HOT: "bg-orange-500 text-white",
  FREE: "bg-[#69C85A] text-white",
  POPULAR: "bg-amber-400 text-amber-950",
};
